import { isWeb, SizableText, YStack } from '@mythical/ui'
import usePagination from 'app/hooks/use-pagination'
import {
  Comment as CommentType,
  createCommentsQuery,
  filterComment,
  mapToComments,
} from 'app/queries/comments/comments'
import { useStores } from 'app/store'
import { observer } from 'mobx-react-lite'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from 'app/components/primitive/Button'
import { BlockModal } from './BlockModal'
import { Comment } from './Comment'
import { CommentForm } from './CommentForm'
import { DeleteModal } from './DeleteModal'
import { ReportModal } from './ReportModal'
import Section from '../structure/Section'
import { Select } from '../primitive/Form'
import Spinner from '../primitive/Spinner'

const parentQuery = createCommentsQuery()

const sorts = [
  { label: 'Latest', value: 'newest' },
  { label: 'Oldest', value: 'oldest' },
  { label: 'Most Liked', value: 'most_liked' },
]

// Helper function to recursively search for a comment ID in nested replies
const findCommentInTree = (
  comments: CommentType[],
  targetId: string,
): boolean => {
  for (const comment of comments) {
    if (comment.id === targetId) return true
    if (comment.replies && comment.replies.length > 0) {
      if (findCommentInTree(comment.replies, targetId)) return true
    }
  }
  return false
}

export type CommentAction = 'delete' | 'report' | 'block'
export type HandleCommentAction = (
  action: CommentAction,
  item: CommentType,
) => void
interface CommentsListProps {
  slug: string
  parent: string
  commentCount: number
  isReplies?: boolean
  targetCommentId?: string
  scrollRef?: any
}

const options = {
  limit: 10,
  formatFn: mapToComments,
}

export const CommentsList = observer(
  ({
    slug,
    parent,
    commentCount = 0,
    isReplies,
    targetCommentId: targetCommentIdProp,
    scrollRef,
  }: CommentsListProps) => {
    const { userStore } = useStores()
    const loggedIn = !!userStore?.user?.id

    const [sort, setSort] = useState(sorts[0]!.value)
    const [deleteModal, setDeleteModal] = useState(false)
    const [reportModal, setReportModal] = useState(false)
    const [blockModal, setBlockModal] = useState(false)
    const [actionedItem, setActionedItem] = useState<CommentType>(
      {} as CommentType,
    )
    const [targetCommentId, setTargetCommentId] = useState<string | null>(
      targetCommentIdProp || null,
    )

    const memoedParams = useMemo(() => {
      return { slug, sort }
    }, [slug, sort])

    const {
      isFetchingNextPage,
      data,
      error,
      hasMore,
      fetchData,
      fetchMore,
      loading,
    } = usePagination(parentQuery, memoedParams, options)

    const docs = (data?.docs || []).filter(filterComment)

    const handleCommentAction: HandleCommentAction = (action, item) => {
      switch (action) {
        case 'delete':
          setActionedItem(item)
          setDeleteModal(true)
          break
        case 'report':
          setActionedItem(item)
          setReportModal(true)
          break
        case 'block':
          setActionedItem(item)
          setBlockModal(true)
          break
      }
    }

    const onUpdate = async () => {
      await fetchData()
    }

    useEffect(() => {
      // Check for commentId in URL param (works on both web and mobile)
      if (targetCommentIdProp) {
        setTargetCommentId(targetCommentIdProp)
      }
    }, [targetCommentIdProp])

    const handleCommentLayout = useCallback(
      (commentId: string, ref: any) => {
        if (commentId !== targetCommentId) return

        // Small delay to ensure layout is complete
        setTimeout(() => {
          if (isWeb) {
            // Web: use scrollIntoView
            const el = document.getElementById(`comment-${targetCommentId}`)
            if (el) {
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          } else if (scrollRef?.current && ref) {
            // Mobile: measure layout and scroll to position
            try {
              ref.measureLayout(
                scrollRef.current.getScrollableNode?.() || scrollRef.current,
                (x: number, y: number) => {
                  scrollRef.current?.scrollTo?.({
                    y: y - 100, // Offset to show comment with some padding
                    animated: true,
                  })
                },
                () => {
                  // Fallback if measureLayout fails
                  console.warn('Could not measure comment layout')
                },
              )
            } catch (e) {
              console.warn('Error measuring comment:', e)
            }
          }
        }, 300)
      },
      [targetCommentId, scrollRef],
    )

    useEffect(() => {
      if (!targetCommentId) return
      if (!docs || docs.length === 0) return

      // Recursively search through comments and their nested replies
      const found = findCommentInTree(docs, targetCommentId)

      // Keep loading more comments until we find the target
      if (!found && hasMore && !isFetchingNextPage) {
        fetchMore()
      }
    }, [docs, targetCommentId, hasMore, isFetchingNextPage, fetchMore])

    return (
      <Section
        title="Comments"
        titleHighlight={`${commentCount}`}
        pb="$3.5"
        customCta={
          <Select
            id="sort-comments"
            hideLabel
            theme="bgSecondaryOutlined"
            name="comments-sort"
            label="Sort by"
            value={sort}
            onValueChange={setSort}
            options={sorts}
            size="$3"
            triggerProps={{ br: '$pill' }}
          />
        }
      >
        <YStack id="comments" width="100%" gap="$3" $gtXs={{ gap: '$4' }}>
          <YStack gap="$2">
            {!isReplies && loggedIn && (
              <CommentForm parent={parent} onSubmitted={fetchData} />
            )}

            {error && (
              <SizableText color="$fgError">{error.message}</SizableText>
            )}

            {docs.map((item: CommentType) => (
              <Comment
                item={item}
                key={item.id}
                level={1}
                handleAction={handleCommentAction}
                onUpdate={onUpdate}
                targetCommentId={targetCommentId}
                onCommentLayout={handleCommentLayout}
              />
            ))}

            {hasMore && docs.length !== 0 && (
              <YStack jc="center" ai="center" pt="$2">
                <Button
                  justifyContent="center"
                  onPress={() => fetchMore()}
                  loading={isFetchingNextPage}
                >
                  Load More
                </Button>
              </YStack>
            )}

            {loading && (
              <YStack jc="center" ai="center" p="$3.5">
                <Spinner size="large" />
              </YStack>
            )}

            <DeleteModal
              modalOpen={deleteModal}
              setModalOpen={setDeleteModal}
              id={actionedItem.id}
              onDelete={() => {
                setDeleteModal(false)
                onUpdate()
              }}
            />

            <BlockModal
              modalOpen={blockModal}
              setModalOpen={setBlockModal}
              id={actionedItem.memberId}
              onBlock={() => {
                setBlockModal(false)
                onUpdate()
              }}
            />

            <ReportModal
              modalOpen={reportModal}
              setModalOpen={setReportModal}
              id={actionedItem.id}
              onReport={() => {
                setReportModal(false)
                onUpdate()
              }}
            />
          </YStack>
        </YStack>
      </Section>
    )
  },
)
